import { DataRole, DataType, DateTime } from '@mode-switch/express';
import { DomainResult } from '@mode-switch/query';
import {
  BasicChannel,
  ColorTarget,
  EncChannel,
  EncField,
  EncFieldGroup,
  Encoding,
  EncodingResult,
  MarkChannel,
  MarkType,
  VariableType,
} from '@mode-vegas/vegas';
import {
  BinSizeTypes,
  ChartDataTypes,
  ChartDomain,
  ChartTypes,
  FlamingoResponse,
  FlamingoTypes,
  ImmutableMap,
} from '@mode/shared/contract-common';
import { Observable } from 'rxjs';
import { RenderDataModel } from '../charts.contract';

// Types should be defined separately from utils, but these dependencies pre-dates contracts.
// Leaving them in place to move forward anyway.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';

export interface DragFieldInfo {
  formula?: CompiledFormula;
  source?: EncChannel | 'field-list';
  fields?: string[];
  groupId?: string;
}

export interface DragInfo {
  field?: CompiledFormula;
  source?: EncChannel | 'field-list';
  valid?: boolean;
}

export abstract class ChartFacade {
  abstract readonly dragField$: Observable<DragFieldInfo | {}>;
  abstract readonly selectedToken$: Observable<ChartTypes.ChartFeatureState['selectedToken']>;
  abstract readonly chartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  abstract readonly versions$: Observable<ChartTypes.ChartFeatureState['versions']>;
  abstract readonly histories$: Observable<ImmutableMap<ChartTypes.ChartHistory, string>>;
  abstract readonly containerHasCharts$: Observable<boolean>;
  abstract readonly v2ChartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  abstract readonly v1ChartTokens$: Observable<ChartTypes.ChartFeatureState['tokens']>;
  abstract readonly isDeletingChart$: Observable<boolean>;
  abstract readonly isCreatingChart$: Observable<boolean>;
  abstract readonly newChartCreatedToken$: Observable<string>;
  abstract readonly hasVizInStorage$: Observable<boolean>;
  abstract readonly savedVizInStorage$: Observable<ChartTypes.ChartSnapshot>;
  abstract createSwitchViewTokenFromVizToken(vizToken: string): Observable<string | undefined>;
  abstract createQueryTokenFromVizToken(vizToken: string): Observable<string | undefined>;
  abstract createChartSnapshot(vizToken: string): Observable<{
    encoding: Readonly<Encoding> | undefined;
    colorPaletteToken: string | undefined;
    format: Readonly<ChartTypes.ChartFormat> | undefined;
    fieldFormats: Readonly<ChartTypes.FieldFormatMap> | undefined;
    fieldColors: Readonly<ChartTypes.FieldColorMap> | undefined;
    facetSizes: Readonly<ChartTypes.FacetSizeMap> | undefined;
    title: Readonly<string | undefined>;
    description: Readonly<string | undefined>;
    caption: Readonly<ChartTypes.CaptionInfo> | undefined;
    chartType: Readonly<ChartTypes.ChartType> | undefined;
    isAutoUpdateDisabled: Readonly<boolean>;
  }>;
  abstract createChartVersionFromVizToken(vizToken: string): Observable<Readonly<1 | 2> | undefined>;
  abstract createIsValidFromVizToken(vizToken: string): Observable<boolean>;
  abstract createIsCompleteFromVizToken(vizToken: string): Observable<boolean>;
  abstract createChartTypeFromVizToken(vizToken: string): Observable<Readonly<ChartTypes.ChartType> | undefined>;
  abstract createEncodingFromVizToken(vizToken: string): Observable<Readonly<Encoding> | undefined>;
  abstract createChartFormatFromVizToken(vizToken: string): Observable<Readonly<ChartTypes.ChartFormat> | undefined>;
  abstract createPagingOffsetFromVizToken(vizToken: string): Observable<number>;
  abstract createChartFieldFormatMapFromVizToken(
    vizToken: string
  ): Observable<Readonly<ChartTypes.FieldFormatMap> | undefined>;
  abstract createChartFieldColorMapFromVizToken(
    vizToken: string
  ): Observable<Readonly<ChartTypes.FieldColorMap> | undefined>;
  abstract createPaletteFromVizToken(vizToken: string): Observable<string | undefined>;
  abstract createChartFacetSizeMapFromVizToken(
    vizToken: string
  ): Observable<Readonly<ChartTypes.FacetSizeMap> | undefined>;
  abstract createChartTitleFromVizToken(vizToken: string): Observable<Readonly<string | undefined>>;
  abstract createChartDescriptionFromVizToken(vizToken: string): Observable<Readonly<string | undefined>>;
  abstract createChartTypeEncodingFromVizToken(vizToken: string): Observable<{
    chartType: Readonly<ChartTypes.ChartType>;
    chartToken: string;
    queryToken: string;
    encoding: Readonly<Encoding>;
  } | null>;
  abstract createIsAutoUpdateDisabledFromVizToken(vizToken: string): Observable<Readonly<boolean>>;
  abstract createManuallyApplyChangesFromVizToken(vizToken: string): Observable<Readonly<boolean>>;
  abstract createUnappliedChangesFromVizToken(vizToken: string): Observable<number>;
  abstract createEphemeralsFromVizToken(vizToken: string): Observable<Readonly<boolean>>;
  abstract createCaptionFromVizToken(vizToken: string): Observable<Readonly<ChartTypes.CaptionInfo> | undefined>;
  abstract createChartsMetadataBySwitchViewToken(switchViewToken: string): Observable<
    {
      chartToken: string;
      title: Readonly<string | undefined>;
      type: Readonly<ChartTypes.ChartType> | undefined;
      viewVersion: Readonly<1 | 2> | undefined;
      legacyType: ChartTypes.V1ChartType | undefined;
      createdAt: string | undefined;
    }[]
  >;
  abstract createChartTokensBySwitchViewToken(switchViewToken: string): Observable<string[]>;
  abstract createIsVizFromImportedSwitchView(vizToken: string): Observable<boolean>;
  abstract createCompleteFlamingoParams(vizToken: string): Observable<{
    chartType: Readonly<ChartTypes.ChartType>;
    chartToken: string;
    switchViewToken: string;
    encoding: Readonly<Encoding>;
    isAutoUpdateDisabled: Readonly<boolean>;
    manuallyApplyChanges: Readonly<boolean>;
  }>;
  abstract createCompleteFlamingoParamsWithEmptyEncoding(vizToken: string): Observable<{
    chartType: Readonly<ChartTypes.ChartType>;
    chartToken: string;
    switchViewToken: string;
    encoding: Readonly<Encoding>;
    isAutoUpdateDisabled: Readonly<boolean>;
    manuallyApplyChanges: Readonly<boolean>;
  }>;
  abstract createRenderDataModel(vizToken: string, planId: string): Observable<RenderDataModel | null | undefined>;
  abstract createManualSortDomainValues(refId: string, alias: string): Observable<readonly any[]>;
  abstract createFlamingoChartSummary(
    vizToken: string,
    planId: string
  ): Observable<ChartDataTypes.Dataset | null | undefined>;
  abstract selectChart(token: string): void;
  abstract loadChartsFromGfResources(queryToken: string, resources: ReadonlyArray<ChartTypes.ChartResource>): void;
  abstract loadChartFromReportView(queryToken: string, resource: ReadonlyArray<ChartTypes.ChartResource>): void;
  abstract startFieldDrag(
    formula: CompiledFormula | null | undefined,
    source: EncChannel | 'field-list',
    fields?: string[],
    groupId?: string
  ): void;
  abstract endFieldDrag(): void;
  abstract createNewChart(switchViewToken: string, queryToken: string, chartType?: ChartTypes.ChartType): void;
  abstract createNewTable(switchViewToken: string, queryToken: string): void;
  abstract createDefaultChart(switchViewToken: string, queryToken: string): void;
  abstract createVegasEncodingChart(switchViewToken: string, queryToken: string): void;
  abstract duplicateChartInBrowser(chartToken: string): void;
  abstract duplicateChartInTab(chartToken: string): void;
  abstract duplicateChartInDesigner(chartToken: string): void;
  abstract duplicateChartInBuilder(chartToken: string): void;
  abstract copyToVizStorage(chartToken: string): void;
  abstract pasteFromVizStorage(switchViewToken: string, queryToken: string): void;
  abstract replaceFromVizStorage(token: string): void;
  abstract updateChartType(chartToken: string, chartType: ChartTypes.ChartType): void;
  abstract updateIsAutoUpdateDisabled(chartToken: string, disable: boolean): void;
  abstract manuallyApplyChanges(chartToken: string): void;
  abstract updateCaption(chartToken: string, caption: ChartTypes.CaptionInfo): void;
  abstract updateTitleInDesigner(chartToken: string, title: string): void;
  abstract updatePaletteInDesigner(chartToken: string, paletteToken: string): void;
  abstract deleteChartInBrowser(chartToken: string): void;
  abstract deleteChartInTabs(chartToken: string): void;
  abstract clearChart(chartToken: string): void;
  abstract swapAxes(chartToken: string): void;
  abstract undo(chartToken: string): void;
  abstract redo(chartToken: string): void;
  abstract drillDown(chartToken: string, target: ChartTypes.MarkTarget): void;
  abstract changeHierarchy(chartToken: string, forward: boolean): void;
  abstract dynamicDrillDown(
    chartToken: string,
    target: ChartTypes.MarkTarget,
    targetFieldId: string,
    drillToField: string,
    dateTruncUnit?: DateTime.Unit
  ): void;
  abstract updateBigValueDescription(chartToken: string, description: string): void;
  abstract updateBigValueTrendLabel(chartToken: string, label: string): void;
  abstract viewCalcFieldSetFieldToFormat(field: EncField | null): void;
  abstract viewCalcFieldAllSetFieldToFormat(field: EncField | null): void;
  abstract vizSettingsComponentRemoveSelectedFieldToFormat(): void;
  abstract sharedAxisEncodingSetGroupToFormat(group: EncFieldGroup): void;
  abstract addFieldAndMakeDual(
    chartToken: string,
    enc: Encoding,
    field: CompiledFormula,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ): void;
  abstract moveFieldAndMakeDual(
    chartToken: string,
    enc: Encoding,
    field: CompiledField,
    channel: BasicChannel,
    leftMark: MarkType,
    rightMark: MarkType
  ): void;
  abstract clearFilters(chartToken: string): void;
  abstract clearSorts(chartToken: string): void;
  abstract clearFormatting(chartToken: string): void;
  abstract quickSort(chartToken: string, descending: boolean): void;
  abstract removeSort(chartToken: string, formula: string): void;
  abstract updateChartFormat(chartToken: string, format: ChartTypes.ChartFormat): void;
  abstract updateTextFormat(
    chartToken: string,
    formula: string,
    pane: ChartTypes.FieldPaneTypes,
    format: ChartTypes.TextFormat
  ): void;
  abstract updateTheme(
    chartToken: string,
    theme: string,
    colorPaletteToken?: string,
    palette?: string[],
    showAlert?: boolean
  ): void;
  abstract updateBaseFormatDisplayName(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.BaseFieldFormat
  ): void;
  abstract updateAxisFormat(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.AxisFieldFormat
  ): void;
  abstract updatePaneFormat(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.PaneFieldFormat
  ): void;
  abstract updateHeaderFormat(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.HeaderFieldFormat
  ): void;
  abstract updateLegendFormat(
    chartToken: string,
    formula: string,
    varType: VariableType,
    format: ChartTypes.LegendFieldFormat
  ): void;
  abstract updateColorField(
    chartToken: string,
    formula: string,
    varType: VariableType,
    colorPaletteToken?: string,
    palette?: string[],
    series?: string,
    colorMap?: ChartTypes.ColorEntries,
    gradient?: ChartTypes.ContinuousGradient,
    unassignedColor?: string,
    colorTarget?: ColorTarget
  ): void;
  abstract updateFacetSizes(chartToken: string, facetSizes: ChartTypes.FacetSizeMap): void;
  abstract requestByManualSortDomain(
    token: string,
    refId: string,
    field: EncField,
    encoding: Encoding,
    items: unknown[] | undefined
  ): void;
  abstract updateChartFromSnapshot(token: string, model?: FlamingoTypes.ModeModel): void;
  abstract setEphemeral(token: string): void;
  abstract fetchFilterDomainValues(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    field: CompiledField,
    refId: string,
    domainAlias: string,
    includeRelevantValuesOnly: boolean
  ): void;
  abstract fetchColorDomainValues(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    field: CompiledField,
    refId: string,
    domainAlias: string,
    series?: string
  ): void;
  abstract createSelectDomainValues(refId: string, domainAlias: string): Observable<DomainResult<DataRole> | undefined>;
  abstract createErrorFromDomainQuery(refId: string): Observable<Readonly<FlamingoResponse.FlamingoErrorResponse>>;
  abstract fetchContinuousMarkDomainRange(
    queryRunToken: string,
    view: FlamingoTypes.ModeView,
    encoding: Encoding,
    refId: string,
    field: CompiledField,
    channel: MarkChannel
  ): void;
  abstract createSelectContinuousMarkDomainRange(
    vizToken: string,
    refId: string,
    fieldIds: string[]
  ): Observable<{ min: number; max: number }>;
  abstract createTableChartEncoding(): Encoding;
  abstract createTableColumns(model: FlamingoTypes.ModeModel): {
    alias: string;
    name: string;
    source: string;
    type: DataType;
  }[];
  abstract createTableChartFormats(columns: FlamingoTypes.ResultColumns): {
    format: ChartTypes.ChartFormat;
    fieldsFormat: ChartTypes.FieldFormatMap;
  };
  abstract addRemainingColumnsToTable(
    enc: Encoding,
    model: FlamingoTypes.ModeModel,
    queryToken: string
  ): EncodingResult | null;
  abstract viewUnaggregatedChartData(token: string): void;
  abstract importDatasetWithChartSuccess(
    accountId: string,
    reportId: string,
    switchViewToken: string,
    vizToken: string,
    queryToken: string,
    chart: ChartDomain.Chart
  ): void;
  abstract getBinSize(token: string, columnName: string, refId: string): void;
  abstract createBinSizeToFieldMapFromVizToken(token: string): Observable<Readonly<BinSizeTypes.BinSizeToFieldMap>>;
  abstract createErrorFromBinSizeQuery(refId: string): Observable<Readonly<FlamingoResponse.FlamingoErrorResponse>>;
}
